import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProgramListingItemGroup } from './utils';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Modal from '@/components/common/Modal';
import { AssessmentRunStatus } from '@/gql/enums';
import useDateFormats from '@/utils/useDateFormats';

interface GroupsModalProps {
    open: boolean;
    onClose: () => void;
    group: ProgramListingItemGroup | null;
}

interface AssessmentRunCardProps {
    name: string;
    startDate: string | Date;
    endDate: string | Date;
    status: AssessmentRunStatus;
    completedCount?: number;
    totalCount?: number;
}

const getStatusBadgeClasses = (status: AssessmentRunStatus) => {
    switch (status) {
        case AssessmentRunStatus.InProgress:
            return 'bg-yellow-50 outline-yellow-600/20 text-yellow-800';
        case AssessmentRunStatus.Ended:
            return 'bg-green-50 outline-green-600/20 text-green-700';
        case AssessmentRunStatus.NotStarted:
            return 'bg-neutral-50 outline-gray-500/10 text-gray-600';
        default:
            return 'bg-neutral-50 outline-gray-500/10 text-gray-600';
    }
};

const AssessmentRunCard = ({
    name,
    startDate,
    endDate,
    status,
    completedCount,
    totalCount,
}: AssessmentRunCardProps) => {
    const { t } = useTranslation('programManagementPage');
    const { formatDateFromTo } = useDateFormats();

    const getStatusText = (status: AssessmentRunStatus): string => {
        switch (status) {
            case AssessmentRunStatus.InProgress:
                return t('groupsModal.status.inProgress');
            case AssessmentRunStatus.Ended:
                return t('groupsModal.status.ended');
            case AssessmentRunStatus.NotStarted:
                return t('groupsModal.status.notStarted');
            default:
                return t('groupsModal.status.unknown');
        }
    };

    return (
        <div className="flex flex-col items-start justify-start gap-2 self-stretch rounded-[10px] bg-neutral-50 p-5">
            <div className="inline-flex items-center justify-between self-stretch">
                <div className="text-base leading-6 font-bold text-neutral-900">{name}</div>
                <div
                    className={`flex items-center justify-start rounded-md px-2 py-0.5 outline outline-1 outline-offset-[-1px] ${getStatusBadgeClasses(status)}`}
                >
                    <div className="text-center text-sm leading-5 font-medium">{getStatusText(status)}</div>
                </div>
            </div>
            <div className="inline-flex h-6 items-center justify-start gap-2.5 self-stretch">
                <div className="text-base leading-6 font-medium text-neutral-500">
                    {formatDateFromTo(startDate, endDate)}
                </div>
                {totalCount !== undefined && completedCount !== undefined && (
                    <>
                        <div className="h-6 w-6 origin-top-left rotate-90 self-stretch outline outline-1 outline-offset-[-0.50px] outline-neutral-300"></div>
                        <div className="text-base leading-6 font-medium text-neutral-500">
                            {completedCount}/{totalCount} {t('groupsModal.completedText')}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const GroupsModal = ({ open, onClose, group }: GroupsModalProps) => {
    const { t } = useTranslation('programManagementPage');

    const assessmentRuns = useMemo(() => {
        return group?.assessmentRuns || [];
    }, [group]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={group?.name}
            largeTitle
            noAction
            className="!w-[512px] !max-w-[512px]"
        >
            <div className="inline-flex w-full flex-col items-start justify-start gap-5">
                {assessmentRuns.length === 0 ? (
                    <EmptyContainer>
                        <Empty title={t('groupsModal.noAssessmentRuns')} />
                    </EmptyContainer>
                ) : (
                    assessmentRuns.map(run => (
                        <AssessmentRunCard
                            key={run.id}
                            name={run.name}
                            startDate={run.startDate}
                            endDate={run.endDate}
                            status={run.status}
                        />
                    ))
                )}
                <div className="w-full text-base leading-6 font-medium text-neutral-500">
                    {t('groupsModal.footerTitle', { count: assessmentRuns.length })}
                </div>
            </div>
        </Modal>
    );
};

export default GroupsModal;
